import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { BadgeCheck, ScanLine } from 'lucide-react';
import './MemberPills.css';

/**
 * MemberPills — the Club member / Club staff toggles on a Members-list row (v35).
 *
 * Tap = toggle, immediately (no modal — "count the taps"). Only rendered when
 * the club runs membership cards, and only on ADULT rows: under-18s and
 * shadow children never get a card or a scan capability, so they never see
 * the pills (the server refuses too). Sponsor accounts get the Club member
 * pill (a sponsor can be a paid member) but never the Club staff pill.
 *
 * Vocabulary (Stuart, 17 Sept): everyone in the app is a COMMUNITY MEMBER;
 * those who've paid are CLUB MEMBERS; the people who work the gate and bar
 * are CLUB STAFF — a flag, not a role, so staff can be community and club
 * members too. Internally the flag is still the `scan_cards` capability.
 */
/**
 * WT-25: does this row show anything here? Lets the row drop an empty footer.
 * Adults only (see above); read-only viewers only see the pills that are on.
 */
export function hasMemberPills(member, { enabled, canEdit }) {
  if (!enabled || !member || !member.is_adult) return false;
  if (canEdit) return true;
  return !!member.is_member || (!!member.can_scan && member.role !== 'sponsor');
}

export default function MemberPills({ clubSlug, member, enabled, canEdit }) {
  const qc = useQueryClient();
  const [err, setErr] = useState('');

  const memberMut = useMutation({
    mutationFn: (isMember) => axios.patch(`/clubs/${clubSlug}/membership/members/${member.id}`, { is_member: isMember }).then(r => r.data),
    onSuccess: () => { setErr(''); qc.invalidateQueries({ queryKey: ['members', clubSlug] }); qc.invalidateQueries({ queryKey: ['membership-admin', clubSlug] }); },
    onError: (e) => setErr(e.response?.data?.error || 'Could not update membership'),
  });
  const scanMut = useMutation({
    mutationFn: (scanCards) => axios.patch(`/clubs/${clubSlug}/membership/members/${member.id}/capabilities`, { scan_cards: scanCards }).then(r => r.data),
    onSuccess: () => { setErr(''); qc.invalidateQueries({ queryKey: ['members', clubSlug] }); qc.invalidateQueries({ queryKey: ['membership-admin', clubSlug] }); },
    onError: (e) => setErr(e.response?.data?.error || 'Could not update scanning'),
  });

  if (!hasMemberPills(member, { enabled, canEdit })) return null;
  const busy = memberMut.isPending || scanMut.isPending;
  const isMember = !!member.is_member;
  const canScan = !!member.can_scan;
  const showScan = member.role !== 'sponsor';

  // WT-25: someone who can't change these (a team manager) sees plain badges
  // for what's true, not greyed buttons that still say "tap to remove".
  if (!canEdit) {
    return (
      <div className="mpills mpills-readonly">
        {isMember && (
          <span className="mpill mpill-badge on" title="Paid club member this season">
            <BadgeCheck size={13} /> Club member
          </span>
        )}
        {showScan && canScan && (
          <span className="mpill mpill-badge mpill-scan on" title="Club staff: scans cards on the gate and bar">
            <ScanLine size={13} /> Club staff
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="mpills" title={err || undefined}>
      <button
        type="button"
        className={`mpill ${isMember ? 'on' : ''}`}
        disabled={!canEdit || busy}
        aria-pressed={isMember}
        title={isMember ? 'Club member this season (paid) — tap to remove' : 'Community member — tap to mark as a paid club member'}
        onClick={() => memberMut.mutate(!isMember)}
      >
        <BadgeCheck size={13} /> Club member
      </button>
      {showScan && (
        <button
          type="button"
          className={`mpill mpill-scan ${canScan ? 'on' : ''}`}
          disabled={!canEdit || busy}
          aria-pressed={canScan}
          title={canScan ? 'Club staff — scans cards on the gate and bar; tap to remove' : 'Tap to flag as club staff (gate / bar) — they can then scan cards from their Wallet'}
          onClick={() => scanMut.mutate(!canScan)}
        >
          <ScanLine size={13} /> Club staff
        </button>
      )}
      {err && <span className="mpill-err">{err}</span>}
    </div>
  );
}
